'use client'

import { CheckCircle, ExpandMore, RadioButtonUnchecked, WarningAmber } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  LinearProgress,
  Link as MuiLink,
  Skeleton,
  Stack,
  Typography
} from '@mui/material'
import { useTranslations } from 'next-intl'
import type { ReactNode } from 'react'

import Link from '~/components/Link'

import type { BillingCompletenessStatus } from './types'

export type CompletenessCardItem = {
  key: string
  status: BillingCompletenessStatus
  message: string
  /** Secondary line, e.g. the backend's specific wording under the localized label. */
  detail?: string
  /** Where to fix it; omitted for backend items that carry no route. */
  href?: string
}

type BillingCompletenessCardProps = {
  score: number
  items: CompletenessCardItem[]
  isLoading?: boolean
  /** Card title; defaults to "Billing setup". */
  title?: string
  /** Header action, e.g. the "Fix with Agent" button on the hubs. */
  action?: ReactNode
  /** Compact variant for the composer panel. */
  dense?: boolean
  /**
   * Hub variant: collapse the checklist behind an accordion (closed by default). Header, score,
   * progress bar, and action stay visible so status is still scannable when collapsed.
   */
  collapsible?: boolean
  /** Called when the user follows an item link (the panel closes itself, the hubs do nothing). */
  onNavigate?: () => void
}

/**
 * Score + checklist answering "what is missing before I can bill customers?". Renders either the
 * client-side checklist (`useBillingSetupCompleteness`) or the backend's `completeness` from a draft.
 */
export default function BillingCompletenessCard({
  score,
  items,
  isLoading,
  title,
  action,
  dense,
  collapsible,
  onNavigate
}: BillingCompletenessCardProps) {
  const t = useTranslations('features.BillingComposer')
  const done = items.filter((item) => item.status === 'done').length
  const heading = title ?? t('completeness_title')

  const summary = isLoading ? (
    <Skeleton width={120} />
  ) : (
    <Typography variant="caption" color="text.secondary">
      {t('completeness_summary', { done, total: items.length, score })}
    </Typography>
  )

  const progress = (
    <LinearProgress
      variant={isLoading ? 'indeterminate' : 'determinate'}
      value={score}
      color={scoreColor(score)}
      aria-label={t('completeness_progressLabel', { score })}
      sx={{ height: 6, borderRadius: 3 }}
    />
  )

  const checklist = isLoading ? (
    <Stack spacing={0.75}>
      <Skeleton height={20} />
      <Skeleton height={20} />
      <Skeleton height={20} width="70%" />
    </Stack>
  ) : (
    <Stack component="ul" spacing={0.75} sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {items.map((item) => (
        <Stack key={item.key} component="li" direction="row" spacing={1} alignItems="flex-start">
          <StatusIcon status={item.status} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {item.href && item.status !== 'done' ? (
              <MuiLink
                component={Link}
                href={item.href}
                variant="body2"
                underline="hover"
                onClick={onNavigate}
                sx={{ display: 'inline' }}
              >
                {item.message}
              </MuiLink>
            ) : (
              <Typography variant="body2" color={item.status === 'done' ? 'text.secondary' : 'text.primary'}>
                {item.message}
              </Typography>
            )}
            {item.detail && (
              <Typography variant="caption" color="text.secondary" component="p">
                {item.detail}
              </Typography>
            )}
          </Box>
        </Stack>
      ))}
    </Stack>
  )

  if (collapsible) {
    return (
      <Card variant="outlined" data-cy="billing-completeness-card">
        <Accordion
          defaultExpanded={false}
          disableGutters
          elevation={0}
          sx={{
            bgcolor: 'transparent',
            '&:before': { display: 'none' },
            '& .MuiAccordionSummary-root': { px: 2, py: 0, minHeight: 0 },
            '& .MuiAccordionSummary-content': { my: 2, display: 'block' },
            '& .MuiAccordionDetails-root': { px: 2, pt: 0, pb: 2 }
          }}
        >
          <AccordionSummary expandIcon={<ExpandMore />} aria-controls="billing-setup-checklist" id="billing-setup-header">
            <Stack spacing={1.5} sx={{ width: '100%', pr: 1 }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6">{heading}</Typography>
                  {summary}
                </Box>
                {/* Keep header actions clickable without toggling the accordion. */}
                {action && (
                  <Box
                    sx={{ flexShrink: 0 }}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    {action}
                  </Box>
                )}
              </Stack>
              {progress}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>{checklist}</AccordionDetails>
        </Accordion>
      </Card>
    )
  }

  return (
    <Card variant="outlined" data-cy="billing-completeness-card">
      <CardContent sx={{ p: dense ? 1.5 : 2, '&:last-child': { pb: dense ? 1.5 : 2 } }}>
        <Stack spacing={dense ? 1 : 1.5}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Box>
              <Typography variant={dense ? 'subtitle2' : 'h6'}>{heading}</Typography>
              {summary}
            </Box>
            {action}
          </Stack>
          {progress}
          {checklist}
        </Stack>
      </CardContent>
    </Card>
  )
}

function StatusIcon({ status }: { status: BillingCompletenessStatus }) {
  const t = useTranslations('features.BillingComposer')
  const sx = { fontSize: 18, mt: 0.125, flexShrink: 0 }
  if (status === 'done') return <CheckCircle color="success" titleAccess={t('completeness_status_done')} sx={sx} />
  if (status === 'warning') return <WarningAmber color="warning" titleAccess={t('completeness_status_warning')} sx={sx} />
  return <RadioButtonUnchecked color="disabled" titleAccess={t('completeness_status_todo')} sx={sx} />
}

function scoreColor(score: number): 'success' | 'warning' | 'error' {
  if (score >= 80) return 'success'
  if (score >= 40) return 'warning'
  return 'error'
}
